/**
 * The Add row — one empty row pinned under a screen the user can add to.
 *
 * When the host says the rows on screen are a list the user owns, the grid gets
 * a last row whose Symbol cell takes a ticker: type, Enter, and it is filed. The
 * island draws the row, runs the editor and reports the text; **the host decides
 * everything else** — what a symbol is, which list it goes into, what "already
 * there" means. Same seam as `TrackListBinding`, for the same reason: this
 * package is hosted in a WebView on three platforms and may not import the web
 * app's symbol index or its list store.
 *
 * ## Pinned, not a row in the data
 *
 * `pinnedBottomRowData`, never a sentinel appended to `rowData`. Everything that
 * makes the grid work walks the row model — `diffRows`, the sort, both filter
 * layers, the row counts, the keyboard cursor, selection — and a fake row in it
 * would have to be excluded from each by hand. A pinned row is outside all of
 * them by construction: AG Grid never sorts, filters, counts or selects one, so
 * the row stays last under any sort and "charts nothing, selects nothing" is not
 * a rule anybody has to remember.
 *
 * Pure, and in `lib/` for `row-cursor.ts`'s reason: the edges are the content,
 * and none of them need a grid to state.
 */

import { TRACK_LIST_FIELD } from "./columns.js";
import { isPlainTypingKey, type TypingKeyEvent } from "./typing-keys.js";

/**
 * The pinned row's id, and the flag on its data.
 *
 * AG Grid calls `getRowId` for pinned rows too (with `rowPinned` set), and a
 * pinned row whose id changes is a new row: its cells — the open editor among
 * them — are torn down and rebuilt. So the id is a constant. Double underscore
 * for `TRACK_LIST_FIELD`'s reason: no server field can be spelled like it.
 */
export const ADD_ROW_ID = "__addRow";

/** Where the last rejection travels, on the pinned row's own data. */
export const ADD_ROW_MESSAGE = "__addRowMessage";

/** What the Symbol cell says before anything is typed into it. */
export const ADD_ROW_PLACEHOLDER = "+ Add or paste symbols…";

/** One completion offered under the cell while typing. */
export interface AddRowSuggestion {
  /** The ticker, as the list would store it. */
  symbol: string;
  /** The company, for the second column of the dropdown. */
  name?: string | null;
  /**
   * The whole box once this is accepted, when that is more than `symbol`.
   *
   * Typing `aapl/sp` and picking SPY must submit `AAPL/SPY`, not `SPY` — and
   * which part of the text a pick replaces is the host's grammar, not the
   * island's. So the host says what the box becomes, and the island submits it.
   */
  value?: string;
}

/** One piece of typed text that did not go in, and the sentence that says why. */
export interface AddRowRejection {
  /** As typed, so it can be put back in the box to fix. */
  text: string;
  /** Shown to the user verbatim — `ZZZZ isn't in the symbol list`. */
  reason: string;
}

export interface AddRowResult {
  /** What went in, in the spelling the list stores. */
  added: readonly string[];
  rejected: readonly AddRowRejection[];
}

/**
 * What the host must supply for the Add row to exist at all.
 *
 * Omitted, and there is no row — which is what every screen that is not a single
 * list of the user's own gets, and what any host that has not built lists gets.
 */
export interface AddRowBinding {
  /**
   * Completions for what is in the box. `caret` travels because the host ranks
   * on the operand under it — `aapl/sp` must offer SPY, and ranking the whole
   * string finds nothing.
   */
  suggest: (text: string, caret: number) => readonly AddRowSuggestion[];
  /**
   * Enter was pressed. Validate, file, and say what happened. Never called for
   * anything but Enter or a picked suggestion — cancelling adds nothing.
   */
  onAdd: (text: string) => Promise<AddRowResult>;
  /** The hint in the empty cell. Defaults to `ADD_ROW_PLACEHOLDER`. */
  placeholder?: string;
}

/** The structural part of an AG Grid row node this reads. */
export interface AddRowNodeLike {
  rowPinned?: "top" | "bottom" | null;
  data?: Record<string, unknown> | null;
}

/** The pinned row's data. The message is the only thing on it that changes. */
export function addRowData(message: string | null): Record<string, string | boolean | null> {
  return { [ADD_ROW_ID]: true, [ADD_ROW_MESSAGE]: message };
}

/**
 * Is this the Add row?
 *
 * By the flag on its data rather than by `rowPinned` alone, so a host that pins
 * a row of its own some day does not find it editable.
 */
export function isAddRowNode(node: AddRowNodeLike | null | undefined): boolean {
  return node?.rowPinned === "bottom" && node.data?.[ADD_ROW_ID] === true;
}

/**
 * Should AG Grid keep its hands off this keystroke, on the Add cell?
 *
 * The grid-wide rule (`isPlainTypingKey`) lets a typed letter *out* of the grid,
 * so the host's type-to-search can have it. On the Add cell that is exactly
 * backwards: a letter typed there is the first letter of a ticker, and it has to
 * reach AG Grid, which starts the editor with it and marks the event handled —
 * which is what makes the host's search stand down. So, not editing: suppress
 * nothing.
 *
 * Editing, the editor owns three keys AG Grid would otherwise act on first (its
 * listener is on the row container, the editor's is React's, at the root):
 *
 * - **Enter** — AG Grid would end the edit. Here it submits and *stays* in the
 *   editor for the next symbol.
 * - **↑ / ↓** — they walk the suggestion list.
 *
 * Escape and Tab stay AG Grid's: both end the edit, and the editor cancels any
 * value on the way out, so nothing is ever added without Enter.
 */
export function addRowSuppressesKey(event: TypingKeyEvent, editing: boolean): boolean {
  if (!editing) return false;
  if (event.key === "Enter" || event.key === "ArrowUp" || event.key === "ArrowDown") return true;
  // A character typed into the input is the input's; AG Grid ignores it while
  // editing anyway, so this only keeps the answer the same as everywhere else.
  return isPlainTypingKey(event);
}

/**
 * Which cell of the Add row carries the rejection.
 *
 * `name`, as the approved mock has it — the sentence sits where the company name
 * would be, beside the ticker it is about. A screen that hides `name` gets the
 * next shown column after the Symbol cell, then the one before it; the star
 * column is skipped both ways, because a sentence in a 44px column is a clipped
 * word. `undefined` only when the Symbol cell is the whole grid.
 */
export function addRowMessageColumn(
  displayed: readonly string[],
  symbolField: string,
): string | undefined {
  if (displayed.includes("name") && symbolField !== "name") return "name";
  const at = displayed.indexOf(symbolField);
  const usable = (id: string) => id !== symbolField && id !== TRACK_LIST_FIELD;
  return displayed.slice(at + 1).find(usable) ?? displayed.slice(0, Math.max(at, 0)).reverse().find(usable);
}

/**
 * What the row shows after an add, and what the box is left holding.
 *
 * Everything went in: no message, an empty box, ready for the next symbol. Some
 * did not: one sentence per rejection, and **the box keeps only those** — a paste
 * of four with one typo comes back as the typo, not as all four again, which
 * would re-send three that are already in.
 */
export function addRowOutcome(result: AddRowResult): { message: string | null; remaining: string } {
  if (result.rejected.length === 0) return { message: null, remaining: "" };
  return {
    message: result.rejected.map((r) => r.reason).join(" · "),
    remaining: result.rejected.map((r) => r.text).join(" "),
  };
}
